import { format, isValid, parseISO } from 'date-fns';
import { wallClockToHour } from './event-helper';
import { getMealFlag } from './string-format-helper';

// O 409 DailyRecords.DayTaken: { error, takenDays }. Null quando o erro é outro.
export const takenDays = (error: any): TakenDay[] | null => {
  const days = error?.status === 409 ? error.body?.takenDays : null;

  return Array.isArray(days) ? days : null;
};

const holderLabel = (day: TakenDay, staffNames: Record<string, string>) => {
  if (day.heldBy === 'Absence') return 'falta';
  if (day.heldBy === 'PublishedDiary') return 'diário';

  return staffNames[day.authorUuid] ? `rascunho de ${staffNames[day.authorUuid]}` : 'rascunho';
};

// staffNames nomeia o autor de um rascunho; a API manda só o uuid.
export const dayTakenMessage = (
  error: any,
  studentNames: Record<string, string>,
  staffNames: Record<string, string> = {}
): string | null => {
  const days = takenDays(error);
  if (!days) return null;

  const taken = days.map(
    (day) =>
      `${studentNames[day.studentUuid] || 'Aluno'} (${holderLabel(day, staffNames)} em ${format(parseISO(day.date), 'dd/MM/yyyy')})`
  );
  const who = taken.length > 0 ? `Estes alunos já têm um registro no dia: ${taken.join(', ')}.` : 'O dia de um dos alunos já tem um registro.';

  return `${who} Nada foi salvo; recarregue a página e tente de novo.`;
};

const joinPt = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

// Só quando todo dia ocupado é de um rascunho: um diário enviado ou uma falta continuam bloqueando,
// e repetir com discardDraft daria o mesmo 409.
export const draftDiscardPrompt = (
  error: any,
  studentNames: Record<string, string>,
  staffNames: Record<string, string> = {}
): string | null => {
  const days = takenDays(error);
  if (!days || days.length === 0 || days.some((day) => day.heldBy !== 'DiaryDraft')) return null;

  const who = days.map((day) => {
    const student = studentNames[day.studentUuid] || 'Um aluno';
    const author = staffNames[day.authorUuid];

    return author ? `${student} está no rascunho de ${author}` : `${student} está em um rascunho`;
  });

  return `${joinPt(who)}. Remover do rascunho e registrar a falta? O rascunho continua para os outros alunos.`;
};

const wallClockTime = (at: EventDateTime) => wallClockToHour(at.local);

export const diaryGroupByline = (group: ClassroomDiaryGroup): string => {
  const byline =
    group.status === 'Draft'
      ? `Rascunho ${group.authorName ? `de ${group.authorName}, ` : ''}iniciado às ${wallClockTime(group.createdAt)}`
      : `Enviado ${group.authorName ? `por ${group.authorName} ` : ''}às ${wallClockTime(group.publishedAt || group.createdAt)}`;
  if (!group.lastEditedAt) return byline;

  const editor = group.lastEditedByName ? `por ${group.lastEditedByName} ` : '';

  return `${byline} · editado ${editor}às ${wallClockTime(group.lastEditedAt)}`;
};

export const absenceByline = (absence: Absence): string =>
  `Registrada ${absence.createdByName ? `por ${absence.createdByName} ` : ''}às ${wallClockTime(absence.createdAt)}`;

export const studentNamesOf = (group: ClassroomDiaryGroup): Record<string, string> =>
  Object.fromEntries(group.entries.map((entry) => [entry.student.uuid, entry.student.name]));

export const draftedStudentUuids = (groups: ClassroomDiaryGroup[]): Set<string> =>
  new Set(
    groups.filter((group) => group.status === 'Draft').flatMap((group) => group.entries.map((entry) => entry.student.uuid))
  );

// O 409 de um dia ocupado traz só o uuid do autor; os nomes vêm dos grupos do dia já carregados.
export const staffNamesOf = (groups: ClassroomDiaryGroup[]): Record<string, string> => {
  const names: Record<string, string> = {};
  groups.forEach((group) => {
    if (group.authorName) names[group.authorUuid] = group.authorName;
    if (group.lastEditedByUuid && group.lastEditedByName) names[group.lastEditedByUuid] = group.lastEditedByName;
  });

  return names;
};

// day e today são dias da escola, "yyyy-MM-dd". A API responde 400 Diary.NotPublishable fora do dia.
export const draftPublishBlock = (day: string, today: string): string | null =>
  day === today
    ? null
    : `Um rascunho só pode ser enviado no próprio dia (${format(parseISO(day), 'dd/MM/yyyy')}). Descarte o rascunho para liberar o dia dos alunos.`;

// O dia de um link "?dia=yyyy-MM-dd"; o dia da turma não vai além de hoje.
export const classroomDayOf = (param: string | null, today: string): string => {
  const date = param && /^\d{4}-\d{2}-\d{2}$/.test(param) ? parseISO(param) : null;

  return date && isValid(date) && format(date, 'yyyy-MM-dd') === param && param <= today ? param : today;
};

export const classroomDayQuery = (day: string, today: string): string => (day === today ? '' : `?dia=${day}`);

const PRESCHOOL_FIELDS: (keyof DiaryContent)[] = [
  'breakfast',
  'lunch',
  'afternoonCoffee',
  'dinner',
  'sleep',
  'evacuate',
  'shower',
  'diaper',
  'details',
];

const fieldsOf = (mode: DiaryMode): (keyof DiaryContent)[] => (mode === 'Preschool' ? PRESCHOOL_FIELDS : ['details']);

const isEmpty = (value: unknown) => value === null || value === undefined || value === '';

const CARE_LABELS: [keyof DiaryContent, string][] = [
  ['sleep', 'Dormiu'],
  ['evacuate', 'Evacuou'],
  ['shower', 'Banho'],
  ['diaper', 'Fralda'],
];

const MEAL_LABELS: [keyof DiaryContent, string][] = [
  ['breakfast', 'Café'],
  ['lunch', 'Almoço'],
  ['afternoonCoffee', 'Lanche'],
  ['dinner', 'Janta'],
];

// Uma linha com o que um diário de creche marcou, sem as observações: "Dormiu 2 · Café: quis todo".
export const contentSummary = (content: DiaryContent): string =>
  [
    ...CARE_LABELS.filter(([field]) => content[field] !== undefined && content[field] !== null).map(
      ([field, label]) => `${label} ${content[field]}`
    ),
    ...MEAL_LABELS.filter(([field]) => content[field]).map(
      ([field, label]) => `${label}: ${getMealFlag(content[field] as MealCompletion).toLowerCase()}`
    ),
  ].join(' · ');

export const contentToFormData = (content: DiaryContent): DiaryFormData['formData'] =>
  Object.fromEntries(PRESCHOOL_FIELDS.map((field) => [field, content[field] ?? null])) as DiaryFormData['formData'];

export const formDataToContent = (formData: DiaryFormData['formData'], mode: DiaryMode): DiaryContent =>
  Object.fromEntries(fieldsOf(mode).filter((field) => !isEmpty(formData[field])).map((field) => [field, formData[field]]));

// Merge patch do PATCH diary-groups/{id}: só os campos que o professor mudou, null para apagar. A
// resposta omite os nulos, então um campo ausente no grupo é um campo vazio.
export const contentPatch = (saved: DiaryContent, edited: DiaryContent, mode: DiaryMode): DiaryContentPatch => {
  const patch: DiaryContentPatch = {};
  fieldsOf(mode).forEach((field) => {
    const before = isEmpty(saved[field]) ? null : saved[field];
    const after = isEmpty(edited[field]) ? null : edited[field];
    if (before !== after) (patch as Record<string, unknown>)[field] = after;
  });

  return patch;
};

// Editar ou enviar parte do grupo separa esses alunos num grupo novo; o grupo inteiro vai sem
// studentUuids, para agir no próprio grupo.
export const splitStudentUuids = (group: ClassroomDiaryGroup, selected: string[]): string[] | undefined =>
  group.entries.every((entry) => selected.includes(entry.student.uuid)) ? undefined : selected;

export const splitNotice = (group: ClassroomDiaryGroup, selected: string[]): string | null => {
  if (selected.length === 0 || !splitStudentUuids(group, selected)) return null;

  const names = (entries: ClassroomDiaryGroupEntry[]) => joinPt(entries.map((entry) => entry.student.name || 'Aluno'));
  const split = group.entries.filter((entry) => selected.includes(entry.student.uuid));
  const rest = group.entries.filter((entry) => !selected.includes(entry.student.uuid));
  const becomes = `${names(split)} ${split.length === 1 ? 'vai' : 'vão'} virar um grupo separado`;
  const stays = `${names(rest)} ${rest.length === 1 ? 'continua' : 'continuam'}`;

  return group.status === 'Draft'
    ? `${becomes}. Salvar cria um rascunho só com a seleção; enviar envia só o diário da seleção. ${stays} neste rascunho como está.`
    : `${becomes}, e só a seleção recebe a edição. ${stays} com o diário como está.`;
};

// Null quando nada mudou: uma edição sem mudança não é enviada.
export const editGroupRequest = (
  group: ClassroomDiaryGroup,
  edited: DiaryContent,
  selected: string[],
  renotify: boolean
): EditDiaryGroupRequest | null => {
  const content = contentPatch(group.content, edited, group.mode);
  if (Object.keys(content).length === 0) return null;

  const request: EditDiaryGroupRequest = { version: group.version, content };
  if (group.status === 'Published') request.renotify = renotify;
  const studentUuids = splitStudentUuids(group, selected);
  if (studentUuids) request.studentUuids = studentUuids;

  return request;
};

// Os 409 do módulo não trazem código, só o texto: { error }.
const conflictText = (error: any): string | null =>
  error?.status === 409 && typeof error.body?.error === 'string' ? error.body.error : null;

// 409 Diary.Changed: a versão enviada já não é a do grupo.
export const isDiaryChanged = (error: any): boolean => Boolean(conflictText(error)?.includes('changed meanwhile'));

const hasValidationCode = (error: any, code: string): boolean =>
  error?.status === 400 && Array.isArray(error.body?.errors) && error.body.errors.some((e: any) => e?.code === code);

// Null quando o erro não é uma das recusas conhecidas das rotas de diary-groups.
export const diaryGroupErrorMessage = (
  error: any,
  studentNames: Record<string, string>,
  staffNames: Record<string, string> = {}
): string | null => {
  if (isDiaryChanged(error)) {
    return 'Um colega salvou este diário enquanto isso. Nada foi salvo; recarregue a página e tente de novo.';
  }
  if (conflictText(error)?.includes('already published')) return 'Este rascunho já foi enviado.';
  if (hasValidationCode(error, 'Diary.NotPublishable')) return 'Um rascunho só pode ser enviado no próprio dia.';
  if (hasValidationCode(error, 'Diary.NotInGroup')) {
    return 'Um dos alunos escolhidos não está mais neste diário: um colega pode ter separado o grupo. Nada foi salvo; recarregue o diário.';
  }
  if (error?.status === 404) return 'O diário não foi encontrado: ele pode ter sido descartado.';

  return dayTakenMessage(error, studentNames, staffNames);
};

// Ao lado dos pendentes, nunca subtraído deles: um aluno em rascunho continua pendente.
export const draftsLabel = (drafts: number): string | null =>
  drafts > 0 ? `${drafts} ${drafts === 1 ? 'rascunho' : 'rascunhos'}` : null;

// A repetição com discardDraft é uma transação só: se falha, nenhuma falta foi registrada.
export const discardDraftErrorMessage = (
  error: any,
  studentNames: Record<string, string>,
  staffNames: Record<string, string> = {}
): string | null => {
  if (error?.status === 403) {
    return (
      'O rascunho é de uma turma que você não acessa, então não pode tirar o aluno dele. ' +
      'Peça a um professor dessa turma ou a um administrador. Nenhuma falta foi registrada.'
    );
  }
  if (isDiaryChanged(error)) {
    return 'Um colega mudou o rascunho enquanto isso. Nenhuma falta foi registrada; recarregue a página e tente de novo.';
  }

  return dayTakenMessage(error, studentNames, staffNames);
};

// O StudentSelector trabalha com ids numéricos e as rotas do módulo só com uuids: o id
// de cada aluno é a posição dele na lista, e o uuid volta por selectedStudents.
export const SELECTOR_CLASSROOM_ID = 0;

export const toSelectorClassroom = (pending: ClassroomPendingStudents): ClassroomStudents =>
  ({
    id: SELECTOR_CLASSROOM_ID,
    name: pending.classroomName || '',
    diaryMode: pending.diaryMode,
    students: pending.students.map((student, index) => ({ id: index, uuid: student.uuid, name: student.name || '' })),
  } as ClassroomStudents);

export const selectedStudents = (classroom: ClassroomStudents, studentIds: number[]): Student[] =>
  classroom.students.filter((student) => studentIds.includes(student.id));
